import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Chip,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  useTheme,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Security as SecurityIcon,
  PictureAsPdf as PdfIcon,
} from '@mui/icons-material';
import { AuditReport } from '../../services/customsAuditService';

interface AuditSummaryProps {
  report: AuditReport;
  onDownloadPdf?: () => void;
}

const AREA_LABELS: Record<string, string> = {
  description: 'Goods Description',
  tariff: 'Tariff Classification',
  price: 'Customs Value',
  origin: 'Origin Verification',
  export_control: 'Export Control',
  physical: 'Physical Validation',
  tampering: 'Document Tampering',
};

const AREA_COLORS: Record<string, string> = {
  description: '#2196f3',
  tariff: '#9c27b0',
  price: '#ff9800',
  origin: '#4caf50',
  export_control: '#f44336',
  physical: '#00bcd4',
  tampering: '#795548',
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'compliant': return 'success';
    case 'requires_review': return 'warning';
    case 'non_compliant': return 'error';
    default: return 'default';
  }
};

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'pass': return <CheckIcon color="success" />;
    case 'warning': return <WarningIcon color="warning" />;
    case 'fail': return <ErrorIcon color="error" />;
    default: return <SecurityIcon color="action" />;
  }
};

const getScoreColor = (score: number) => {
  if (score >= 60) return 'error';
  if (score >= 30) return 'warning';
  return 'success';
};

const AuditSummary: React.FC<AuditSummaryProps> = ({ report, onDownloadPdf }) => {
  const theme = useTheme();

  return (
    <Box>
      {/* Overall Status Header */}
      <Paper
        sx={{
          p: 3,
          mb: 3,
          background: report.overall_status === 'compliant'
            ? 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)'
            : report.overall_status === 'requires_review'
            ? 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)'
            : 'linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h5" fontWeight={700}>
              Customs Audit Report
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {report.summary}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h3" fontWeight={700} color={`${getScoreColor(report.overall_risk_score)}.main`}>
              {report.overall_risk_score}
            </Typography>
            <Typography variant="caption" color="text.secondary">Risk Score</Typography>
            <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                label={report.overall_status.replace('_', ' ').toUpperCase()}
                color={getStatusColor(report.overall_status) as any}
                sx={{ fontWeight: 'bold' }}
              />
              {report.id && onDownloadPdf && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<PdfIcon />}
                  onClick={onDownloadPdf}
                  sx={{ fontSize: '0.7rem', textTransform: 'none' }}
                >
                  PDF
                </Button>
              )}
            </Box>
          </Box>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Processed in {report.processing_time_ms}ms
        </Typography>
      </Paper>

      {/* Area Score Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {report.area_results.map((area) => (
          <Grid item xs={12} sm={6} md={3} key={area.area}>
            <Paper
              sx={{
                p: 2,
                borderLeft: `4px solid ${AREA_COLORS[area.area] || theme.palette.primary.main}`,
                height: '100%',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  {AREA_LABELS[area.area] || area.area}
                </Typography>
                {getStatusIcon(area.status)}
              </Box>
              <Typography variant="h4" fontWeight={700} color={`${getScoreColor(area.score)}.main`}>
                {area.score}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={area.score}
                color={getScoreColor(area.score) as any}
                sx={{ height: 6, borderRadius: 3, mt: 1 }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {area.findings.length} finding{area.findings.length !== 1 ? 's' : ''}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Critical Findings */}
      {report.critical_findings.length > 0 && (
        <Paper sx={{ p: 2, mb: 3, borderLeft: `4px solid ${theme.palette.error.main}` }}>
          <Typography variant="subtitle1" fontWeight={700} color="error.main" gutterBottom>
            Critical Findings ({report.critical_findings.length})
          </Typography>
          <List dense disablePadding>
            {report.critical_findings.map((finding, idx) => (
              <ListItem key={idx} disableGutters>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <ErrorIcon color="error" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={finding} primaryTypographyProps={{ variant: 'body2' }} />
              </ListItem>
            ))}
          </List>
        </Paper>
      )}
    </Box>
  );
};

export default AuditSummary;
